'use client';

import React, { useCallback, useMemo, useState } from 'react';

import Link from 'next/link';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { FollowButton } from '@/components/FollowButton';
import { InfoCard } from '@/components/cards';
import { IdentityChipGroup, IdentityRow, identityFromDisplayUser } from '@/components/identity';
import { useUserInfo } from '@/context/UserInfoContext';
import { getRowExitStyles, useDismissibleRows } from '@/hooks/useDismissibleRows';
import { useFollowSuggestions } from '@/hooks/useFollowSuggestions';

import HomeAddFriendsPeopleSearch from './HomeAddFriendsPeopleSearch';

const COLLAPSED_SUGGESTION_LIMIT = 2;
const EXPANDED_SUGGESTION_LIMIT = 5;

/**
 * Renders the authenticated homepage add-friends card above the feed.
 *
 * The card stays collapsed by default, previews dynamic suggestions, and expands
 * into suggested follows, inline people search, and an invite call to action.
 */
const HomeAddFriendsCard: React.FC = () => {
  const { userInfo, reloadUserInfo } = useUserInfo();
  const [expanded, setExpanded] = useState(false);

  /** Refreshes user info after a suggestion row finishes dismissing. */
  const handleSuggestionDismissComplete = useCallback(() => {
    reloadUserInfo();
  }, [reloadUserInfo]);

  const {
    dismissedUsernames: dismissedSuggestionUsernames,
    dismissRow,
    isRowExiting,
  } = useDismissibleRows({ onDismissComplete: handleSuggestionDismissComplete });
  const { suggestions, isLoading, error } = useFollowSuggestions({
    userInfo,
    excludedUsernames: dismissedSuggestionUsernames,
  });

  const expandedSuggestions = useMemo(
    () => suggestions.slice(0, EXPANDED_SUGGESTION_LIMIT),
    [suggestions]
  );
  const collapsedSuggestionIdentities = useMemo(
    () => expandedSuggestions.map((suggestedUser) => identityFromDisplayUser(suggestedUser)),
    [expandedSuggestions]
  );

  if (!userInfo) {
    return null;
  }

  return (
    <Box
      component="section"
      sx={{ width: '100%', mb: 3 }}
      aria-labelledby="home-add-friends-heading"
    >
      <InfoCard
        testId="home-add-friends-card"
        sx={{
          '& .MuiCardHeader-root': {
            py: 1.25,
          },
          '& .MuiCardHeader-content': {
            minWidth: 0,
          },
          '& .MuiCardContent-root': expanded
            ? {
                pt: 0,
              }
            : {
                p: 0,
                '&:last-child': {
                  pb: 0,
                },
              },
        }}
        title={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flex: '1 1 18rem',
                minWidth: 0,
                flexWrap: 'wrap',
              }}
            >
              <Typography
                id="home-add-friends-heading"
                variant="h6"
                component="h2"
                sx={{ flexShrink: 0 }}
              >
                Add friends
              </Typography>
              {!expanded ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    minWidth: 0,
                    flex: '1 1 auto',
                    flexWrap: 'wrap',
                  }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress size={16} aria-label="Loading add friends suggestions" />
                      <Typography variant="body2" color="text.secondary">
                        Loading suggestions…
                      </Typography>
                    </>
                  ) : null}
                  {!isLoading && error ? (
                    <Typography variant="body2" color="text.secondary">
                      Suggestions unavailable right now.
                    </Typography>
                  ) : null}
                  {!isLoading && !error && expandedSuggestions.length > 0 ? (
                    <IdentityChipGroup
                      identities={collapsedSuggestionIdentities}
                      maxVisible={COLLAPSED_SUGGESTION_LIMIT}
                      chipProps={{ atPrefix: true, linkMode: 'link' }}
                      overflowChipProps={{
                        'aria-label': 'Show more add friends suggestions',
                        onClick: () => setExpanded(true),
                      }}
                    />
                  ) : null}
                  {!isLoading && !error && expandedSuggestions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Search for people to follow.
                    </Typography>
                  ) : null}
                </Box>
              ) : null}
            </Box>
            <Button
              onClick={() => setExpanded((previousExpanded) => !previousExpanded)}
              size="small"
              variant="text"
              aria-expanded={expanded}
              aria-controls="home-add-friends-panel"
              aria-label={expanded ? 'Hide add friends' : 'Show add friends'}
              sx={{ ml: 'auto', flexShrink: 0 }}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    color: expanded ? 'primary.main' : 'text.secondary',
                    transition: 'color 150ms ease',
                  }}
                />
              }
            >
              {expanded ? 'Hide' : 'Show'}
            </Button>
          </Box>
        }
      >
        <Collapse in={expanded} unmountOnExit id="home-add-friends-panel">
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle1" component="h3" sx={{ mb: 1 }}>
                Suggested follows
              </Typography>
              {isLoading ? (
                <CircularProgress size={20} aria-label="Loading suggested follows" />
              ) : null}
              {!isLoading && error ? (
                <Alert severity="error">Error loading suggested follows.</Alert>
              ) : null}
              {!isLoading && !error && expandedSuggestions.length > 0 ? (
                <List dense disablePadding aria-label="Add friends suggestions">
                  {expandedSuggestions.map((user, index) => (
                    <ListItem
                      key={user.username}
                      disableGutters
                      divider={index < expandedSuggestions.length - 1}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        ...getRowExitStyles(isRowExiting(user.username)),
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <IdentityRow
                          identity={identityFromDisplayUser(user)}
                          linkMode="link"
                          avatarSize={32}
                          showFullname={true}
                        />
                      </Box>
                      <FollowButton
                        authUsername={userInfo.username}
                        followUsername={user.username}
                        currentlyFollowing={false}
                        noTopicPrompt={true}
                        topics={[]}
                        onDirectFollowFeedbackComplete={() => dismissRow(user.username)}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : null}
              {!isLoading && !error && expandedSuggestions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No suggestions right now — try searching below.
                </Typography>
              ) : null}
            </Box>

            <Box>
              <Typography variant="subtitle1" component="h3" sx={{ mb: 1 }}>
                Search people
              </Typography>
              <HomeAddFriendsPeopleSearch userInfo={userInfo} reloadUserInfo={reloadUserInfo} />
            </Box>

            {userInfo.may_invite ? (
              <>
                <Divider />
                <Stack spacing={1}>
                  <Typography variant="subtitle1" component="h3">
                    Invite friends
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Know someone who should join General Wisdom?
                  </Typography>
                  <Box>
                    <Button component={Link} href="/invite" variant="contained">
                      Invite friends
                    </Button>
                  </Box>
                </Stack>
              </>
            ) : null}
          </Stack>
        </Collapse>
      </InfoCard>
    </Box>
  );
};

export default HomeAddFriendsCard;
